import { Col, Radio, Row, Space } from "antd";
import React, { useState, useEffect, memo } from "react";
import Display from "./Display";
import { setFrequency, setEndTime, setStartTime } from "reducers/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  endOfDay,
  getHours,
  isAfter,
  isWithinInterval,
  startOfDay,
} from "date-fns";

const DaysPicker = memo(
  ({
    callApis,
    localTimeState = true,
    timeRange,
    onTimeChange,
    customOptions = ["Hour", "Day", "Week"],
  }) => {
    const dispatch = useDispatch();
    const filter = useSelector((state) => state.filter);
    const [selectedTime, setSelectedTime] = useState(localTimeState ? customOptions[0] : filter.frequency);
    const [nextDisable, setNextDisable] = useState(false);
    const [previousDisable, setPreviousDisable] = useState(false);
    // const startTime = timeRange?.[0] || filter.startTime;
    // const endTime = timeRange?.[1] || filter.endTime;
    const now = new Date().getTime();
    const [localTimeRange, setLocalTimeRange] = useState([
      addHours(now, -1).getTime(),
      now,
    ]);
    let startTime = localTimeState ? localTimeRange?.[0] : filter.startTime;
    let endTime = localTimeState ? localTimeRange?.[1] : filter.endTime;

    const [shouldCallApis, setShouldCallApis] = useState(false);
    let updatedStartTime, updatedEndTime;

    const onTimeRange = (value) => {
      if (onTimeChange && localTimeState) {
        onTimeChange(value);
        setLocalTimeRange([value?.[0], value?.[1]]);
      } else {
        dispatch(setFrequency(selectedTime));
        dispatch(setEndTime(value?.[1]));
        dispatch(setStartTime(value?.[0]));
      }
    };

    const updateDisplayedTime = () => {
      const now = !endTime ? new Date().getTime() : endTime;
      const ninetyDaysBefore = addMonths(new Date().getTime(), -3);
      // const now = endTime;
      switch (selectedTime) {
        case "Hour":
          onTimeRange([addHours(now, -1).getTime(), now]);
          isNextDisabled(now);
          break;
        case "Day":
          onTimeRange([addDays(now, -1).getTime(), now]);
          isNextDisabled(now);
          break;
        case "Week":
          const startWeekDay = addWeeks(now, -1).getTime();
          if (!isAfter(startWeekDay, ninetyDaysBefore)) {
            onTimeRange([
              addWeeks(ninetyDaysBefore, 0),
              addWeeks(ninetyDaysBefore, 1),
            ]);
          } else {
            onTimeRange([startWeekDay, now]);
          }
          isNextDisabled(now);
          break;
        case "Month":
          const startMonthDay = addMonths(now, -1).getTime();
          if (!isAfter(startMonthDay, ninetyDaysBefore)) {
            onTimeRange([
              addMonths(ninetyDaysBefore, 0),
              addMonths(ninetyDaysBefore, 1),
            ]);
          } else {
            onTimeRange([startMonthDay, now]);
          }
          isNextDisabled(now);
          break;
        default:
          break;
      }
    };

    useEffect(() => {
      updateDisplayedTime();
      if (shouldCallApis && callApis) {
        callApis();
        setShouldCallApis(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTime]);

    const handleButtonClick = (decrement) => {
      const timeIncrement = decrement ? -1 : 1;
      const currentDate = new Date();

      switch (selectedTime) {
        case "Hour":
          updatedStartTime = addHours(startTime, timeIncrement);
          updatedEndTime = addHours(endTime, timeIncrement);
          break;
        case "Day":
          updatedStartTime = addDays(startTime, timeIncrement);
          updatedEndTime = addDays(endTime, timeIncrement);
          break;
        case "Week":
          updatedStartTime = addDays(startTime, timeIncrement * 7);
          updatedEndTime = addDays(endTime, timeIncrement * 7);
          break;
        case "Month":
          let newStartTime = addMonths(startTime, timeIncrement);
          let ninetyDaysBefore = addMonths(currentDate, -3);
          if (!isAfter(newStartTime, ninetyDaysBefore)) {
            updatedStartTime = addMonths(ninetyDaysBefore, 0);
            updatedEndTime = addMonths(ninetyDaysBefore, 1);
          } else {
            updatedStartTime = addMonths(startTime, timeIncrement);
            updatedEndTime = addMonths(endTime, timeIncrement);
          }
          break;
        default:
          break;
      }

      isNextDisabled(updatedEndTime);
      isPreviousDisabled(updatedEndTime, updatedStartTime);
      onTimeRange([updatedStartTime.getTime(), updatedEndTime.getTime()]);
      setShouldCallApis(true);
    };

    const isNextDisabled = (endTime) => {
      switch (selectedTime) {
        case "Hour":
          setNextDisable(getHours(endTime) >= getHours(new Date()));
          break;
        case "Day":
        case "Month":
        case "Week":
          const today = startOfDay(new Date());
          const isSameDayAsToday = isAfter(endTime, today);
          setNextDisable(isSameDayAsToday);
          break;
        default:
          setNextDisable(false);
      }
    };

    const isPreviousDisabled = (endTime, startTime) => {
      const now = new Date();
      const threeMonthsStart = addDays(now, -90);

      const interval = { start: startTime, end: endTime };

      switch (selectedTime) {
        case "Hour":
          setPreviousDisable(getHours(endTime) === 1);
          break;
        case "Day":
          setPreviousDisable(!isAfter(endTime, threeMonthsStart));
          break;
        case "Week":
          let thirtyDaysAgoStartOfDay = startOfDay(addDays(now, -90));
          let thirtyDaysAgoEndOfDay = endOfDay(addDays(now, -90));
          setPreviousDisable(
            isWithinInterval(thirtyDaysAgoStartOfDay, interval) ||
              isWithinInterval(thirtyDaysAgoEndOfDay, interval)
          );
          break;
        case "Month":
          let daysAgoStartOfDay = startOfDay(addDays(now, -90));
          let daysAgoEndOfDay = endOfDay(addDays(now, -90));
          setPreviousDisable(
            isWithinInterval(daysAgoStartOfDay, interval) ||
              isWithinInterval(daysAgoEndOfDay, interval)
          );
          break;
        default:
          setPreviousDisable(false);
      }
    };

    const onFilterChange = (e) => {
      setSelectedTime(e.target.value);
      setPreviousDisable(false);
      setNextDisable(false);
    };

    return (
      <Row gutter={[0, 8]} className="days-picker">
        <Col span={24}>
          <Space wrap>
            {customOptions.map((item, index) => (
              <div key={index} className="flex-center">
                <Radio.Group
                  options={[item]}
                  onChange={(e) => onFilterChange(e)}
                  className="days-picker-option"
                  value={selectedTime}
                  optionType="button"
                  size="large"
                  buttonStyle="solid"
                />
                {selectedTime === item && startTime && endTime && (
                  <Display
                    timeRange={[startTime, endTime]}
                    selectedTime={selectedTime}
                    handleButtonClick={handleButtonClick}
                    nextDisable={nextDisable}
                    previousDisable={previousDisable}
                  />
                )}
              </div>
            ))}
          </Space>
        </Col>
      </Row>
    );
  }
);

export default DaysPicker;
